import { ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * Renders an "Open in Practice Manager" external link with the related entity id
 * shown as a copyable monospace pill. Because PM doesn't expose deep-linkable URLs,
 * we open it at top level and let the user paste the id into PM's search.
 *
 * Props:
 *   url       — tenant.practice_manager_url
 *   label     — tenant.practice_manager_label (defaults to "Practice Manager")
 *   entityId  — optional, the id staff should search for in PM
 *   entityKind — "job" | "client" — used for the tooltip / aria label
 *   size      — "sm" | "md"
 */
export default function PracticeManagerLink({
    url,
    label,
    entityId,
    entityKind = "job",
    size = "sm",
    className = "",
    testId,
}) {
    if (!url) return null;
    const safeLabel = label || "Practice Manager";
    const small = size === "sm";

    async function copyId(e) {
        e?.preventDefault();
        e?.stopPropagation();
        try {
            await navigator.clipboard.writeText(entityId);
            toast.success(`${entityKind === "client" ? "Customer" : "Job"} ID copied — paste into Practice Manager search`);
        } catch (_) {
            window.prompt("Copy this ID:", entityId);
        }
    }

    return (
        <div className={`inline-flex items-center gap-1 ${className}`} data-testid={testId}>
            <a
                href={url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className={`inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors ${small ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"}`}
                title={`Open ${safeLabel} (${entityKind === "client" ? "customer" : "job"}${entityId ? ` ${entityId}` : ""})`}
            >
                <ExternalLink className={small ? "h-3 w-3" : "h-3.5 w-3.5"} />
                Open in PM
            </a>
            {entityId && (
                <button
                    type="button"
                    onClick={copyId}
                    className={`inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white text-[#5E636E] hover:bg-zinc-50 font-mono ${small ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-[11px]"}`}
                    title={`Copy ${entityKind} id`}
                >
                    {entityId}
                    <Copy className={small ? "h-2.5 w-2.5" : "h-3 w-3"} />
                </button>
            )}
        </div>
    );
}
